import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import { createService, getPaginatedFilteredPathParam } from "@/api/shared";
import type { NoPathParams } from "@/api/shared";
import type { TPhone } from "@/api/shared/contacts.ts";
import type { TPatient } from "@/api/models/patient/patient";

const SLUG = "patient";

export enum EActiveTreatmentType {
  SELECT_TREATMENT = "select_treatment",
  COTREATMENT = "cotreatment",
  REFERRAL = "referral",
  REFERRAL_FREE = "referral_free",
  REFERRAL_INTERNAL = "referral_internal",
  REFERRAL_PAID = "referral_paid",
}

export enum EActiveTreatmentFilter {
  SELECT_TREATMENT = "select_treatment",
  REFERRAL = "referral",
  COTREATMENT = "cotreatment",
}

export const ACTIVE_TREATMENT_FILTER_MAP = {
  [EActiveTreatmentFilter.SELECT_TREATMENT]: [EActiveTreatmentType.SELECT_TREATMENT],
  [EActiveTreatmentFilter.REFERRAL]: [
    EActiveTreatmentType.REFERRAL,
    EActiveTreatmentType.REFERRAL_FREE,
    EActiveTreatmentType.REFERRAL_INTERNAL,
    EActiveTreatmentType.REFERRAL_PAID,
  ],
  [EActiveTreatmentFilter.COTREATMENT]: [EActiveTreatmentType.COTREATMENT],
} satisfies Record<EActiveTreatmentFilter, EActiveTreatmentType[]>;

export interface IPatientDashboardFilters extends DefaultPaginatedFilters {
  active_treatment_types?: EActiveTreatmentType[];
  language?: string;
  language__in?: string[];
  last_update__lte?: string;
  last_update__gte?: string;
  ordering?: "last_name" | "-last_name" | "last_update" | "-last_update";
  paginate?: boolean;
  /** Narrow the dashboard to patients registered through one practice QR code (QR id). */
  qr_code?: string;
  archived?: boolean;
}

export type TPatientDashboardItem = Pick<
  TPatient,
  | "id"
  | "is_patient"
  | "first_name"
  | "last_name"
  | "middle_name"
  | "sex"
  | "language"
  | "picture"
  | "picture_default"
  | "hrid"
  | "tags"
> & {
  phone_contact: TPhone | null;
  active_treatment_types: EActiveTreatmentType[];
  last_update: string;
};

export default createService(SLUG, {
  dashboard: getPaginatedFilteredPathParam<
    TPatientDashboardItem,
    NoPathParams,
    IPatientDashboardFilters
  >(`${SLUG}/dashboard/`, `${SLUG}/dashboard/search/`),
});
